// Spec moment #2 — full-screen unlock celebration. Mounts when the
// practice answer that just landed completed a topic for the first time.
//
//   - dim gradient fade-in
//   - lineart spring-scales in from center with a soft halo
//   - 解鎖音效 + first confetti burst
//   - "現在來上色" jumps to /pack/:packId/color/:itemId
//   - "稍後再說" advances to the next question (the lineart is already in
//     user_items, so "存進 inventory" needs no further work)
//
// Mount only ever fires for one item per render — parent unmounts immediately
// after onColorNow / onLater so we don't have to handle re-entrancy.

import { useEffect } from 'react'
import { motion } from 'framer-motion'
import { assetUrl, type PackId } from '@skena/schema'
import { fireConfetti } from './confetti.ts'
import { playUnlock } from './sounds.ts'

const PACK_HALO: Record<PackId, string> = {
  joseph: 'shadow-[0_0_60px_18px_rgba(245,214,138,0.55)]',
  nativity: 'shadow-[0_0_60px_18px_rgba(245,197,66,0.55)]',
}

const PACK_BTN_PRIMARY: Record<PackId, string> = {
  joseph: 'bg-[var(--color-joseph-primary)] text-white',
  nativity: 'bg-[var(--color-nativity-primary)] text-white',
}

interface UnlockOverlayProps {
  packId: PackId
  itemId: string
  nameZh: string
  onColorNow: () => void
  onLater: () => void
}

export function UnlockOverlay({ packId, itemId, nameZh, onColorNow, onLater }: UnlockOverlayProps) {
  useEffect(() => {
    playUnlock()
    // Center burst — overlay is full-screen, the lineart sits at viewport center.
    fireConfetti(packId, 0.5, 0.45)
  }, [packId])

  return (
    <motion.div
      role="dialog"
      aria-modal="true"
      aria-labelledby="unlock-headline"
      className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-8 bg-gradient-to-b from-black/85 via-black/75 to-black/85 px-6 backdrop-blur-sm"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <motion.h2
        id="unlock-headline"
        className="text-center text-2xl font-semibold text-white sm:text-3xl"
        initial={{ y: -12, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.15, duration: 0.35 }}
      >
        ✨ 你蒐集到了「{nameZh}」的線條稿！
      </motion.h2>

      <motion.div
        className={`rounded-[var(--radius-soft)] bg-white p-3 ${PACK_HALO[packId]}`}
        initial={{ scale: 0.55, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 240, damping: 22, delay: 0.05 }}
      >
        <img
          src={assetUrl(packId, itemId, 'lineart')}
          alt={nameZh}
          className="block h-auto max-h-[55vh] w-auto max-w-[80vw] object-contain"
          draggable={false}
        />
      </motion.div>

      <motion.div
        className="flex flex-col items-center gap-3 sm:flex-row"
        initial={{ y: 12, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.35, duration: 0.3 }}
      >
        <button
          onClick={onColorNow}
          className={`min-w-[10rem] rounded-full px-6 py-3 text-base font-semibold shadow-md transition-transform hover:scale-[1.03] focus-visible:outline-2 focus-visible:outline-offset-2 ${PACK_BTN_PRIMARY[packId]}`}
        >
          現在來上色
        </button>
        <button
          onClick={onLater}
          className="rounded-full px-6 py-3 text-base text-white/80 underline-offset-4 hover:text-white hover:underline focus-visible:outline-2 focus-visible:outline-offset-2"
        >
          稍後再說
        </button>
      </motion.div>
    </motion.div>
  )
}
